import { Alert, Box, Typography } from "@mui/material";
import { useTranslate } from "react-admin";

import { type LogisticsHexnodeDeviceFragment, LogisticsHexnodeState } from "../../generated/graphql";
import { CRM_COLORS, formatCrmDateTime } from "../../lib/crm";

export const inventoryHexnodeUrl = (
  type: string | null | undefined,
  hexnode: LogisticsHexnodeDeviceFragment | null | undefined
): string | null =>
  type === "TV Box" && hexnode?.state === LogisticsHexnodeState.Linked ? (hexnode.url ?? null) : null;

export const InventoryHexnode = ({
  hexnode,
  compact = false,
}: {
  hexnode?: LogisticsHexnodeDeviceFragment | null;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const unknown = translate("logistics.hexnode.unknown");
  const state = hexnode?.state ?? LogisticsHexnodeState.Unavailable;
  const enrollment = hexnode?.enrollmentStatus;
  const enrollmentKey = enrollment?.toLowerCase();
  const fields: [string, string][] = [];
  if (state === LogisticsHexnodeState.Linked) {
    fields.push(
      [
        translate("logistics.hexnode.enrollment"),
        enrollmentKey === "enrolled" || enrollmentKey === "disenrolled"
          ? translate(`logistics.hexnode.${enrollmentKey}`)
          : enrollment || unknown,
      ],
      [
        translate("logistics.hexnode.activity"),
        hexnode?.active === true
          ? translate("logistics.hexnode.active")
          : hexnode?.active === false
            ? translate("logistics.hexnode.inactive")
            : unknown,
      ],
      [translate("logistics.hexnode.lastSeenAt"), hexnode?.lastSeenAt ? formatCrmDateTime(hexnode.lastSeenAt) : unknown]
    );
  }
  fields.push([
    translate("logistics.hexnode.syncedAt"),
    hexnode?.syncedAt ? formatCrmDateTime(hexnode.syncedAt) : unknown,
  ]);

  return (
    <Box sx={{ display: "grid", gap: compact ? "4px" : "12px" }}>
      {state !== LogisticsHexnodeState.Linked &&
        (compact ? (
          <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>
            {translate(`logistics.hexnode.states.${state}`)}
          </Typography>
        ) : (
          <Alert severity="info">{translate(`logistics.hexnode.states.${state}`)}</Alert>
        ))}
      {hexnode?.stale && (
        <Alert severity="warning" sx={compact ? { p: "0 8px", fontSize: "12px" } : undefined}>
          {translate("logistics.hexnode.stale")}
        </Alert>
      )}
      <Box
        component="dl"
        sx={{
          display: "grid",
          gridTemplateColumns: compact ? "max-content 1fr" : "minmax(140px, max-content) 1fr",
          gap: compact ? "2px 8px" : "6px 16px",
          m: 0,
        }}
      >
        {fields.map(([label, value]) => (
          <Box key={label} sx={{ display: "contents" }}>
            <Typography component="dt" sx={{ fontSize: compact ? "12px" : "13px", color: CRM_COLORS.label }}>
              {label}
            </Typography>
            <Box
              component="dd"
              aria-label={label}
              sx={{ m: 0, fontSize: compact ? "12px" : "13px", color: CRM_COLORS.text, overflowWrap: "anywhere" }}
            >
              {value}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
